import { useMemo, useState } from "react";

import { Button } from "src/base-components/Button";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { isMatchingSourceRef } from "src/cases/createCase/createCaseSteps";
import { useResolvedTemplateVersion } from "src/cases/queries/useResolvedTemplateVersion";
import { DeserializedCaseQueue } from "src/cases/queues/types";
import { Callout } from "src/design-system/Callout";
import { Modal } from "src/design-system/Modal";

type SourceType = "entity" | "event";

type SelectQueueModalProps = {
  open: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  sourceType: SourceType;
  sourceSchemaId: string;
  queues: DeserializedCaseQueue[];
  onSelectQueue: (queueId: string) => void;
};

const getWarningText = (sourceType: SourceType) => {
  const sourceLabel = sourceType === "entity" ? "Entity" : "Event";
  return `The case template this queue uses does not include a matching property. You can still create a case, but it won't be linked to this ${sourceLabel}.`;
};

const useHasMatchingSourceRef = (
  queue: DeserializedCaseQueue | undefined,
  sourceSchemaId: string,
  sourceType: SourceType,
) => {
  const { version } = useResolvedTemplateVersion(queue);

  return useMemo(() => {
    if (!version) return true; // Don't show warning if we can't resolve

    return Object.values(version.attributes.case_properties).some((prop) =>
      isMatchingSourceRef(prop, sourceType, sourceSchemaId),
    );
  }, [version, sourceSchemaId, sourceType]);
};

const QueueWarningCallout: React.FC<{
  queue: DeserializedCaseQueue | undefined;
  sourceSchemaId: string;
  sourceType: SourceType;
}> = ({ queue, sourceSchemaId, sourceType }) => {
  const hasMatch = useHasMatchingSourceRef(queue, sourceSchemaId, sourceType);

  if (hasMatch) return null;

  return (
    <div className="mt-5">
      <Callout variant="warning">{getWarningText(sourceType)}</Callout>
    </div>
  );
};

export const SelectQueueModal: React.FC<SelectQueueModalProps> = ({
  open,
  onClose,
  afterLeave,
  sourceType,
  sourceSchemaId,
  queues,
  onSelectQueue,
}) => {
  const [selectedQueueId, setSelectedQueueId] = useState<string | undefined>();

  const selectedQueue = queues.find((q) => q.id === selectedQueueId);

  const handleCreate = () => {
    if (selectedQueueId) {
      onSelectQueue(selectedQueueId);
      onClose();
    }
  };

  const handleAfterLeave = () => {
    setSelectedQueueId(undefined);
    afterLeave?.();
  };

  return (
    <Modal afterLeave={handleAfterLeave} open={open} onClose={onClose}>
      <Modal.Header description="You can only create cases in queues you're a member of.">
        Select a queue for this Case
      </Modal.Header>
      <Modal.Content>
        <SimpleRadioGroup
          orientation="vertical"
          value={selectedQueueId}
          onValueChange={setSelectedQueueId}
        >
          {queues.map((queue) => (
            <SimpleRadioGroup.Item
              key={queue.id}
              dataLoc={`queue-option-${queue.id}`}
              label={
                <span className="ml-2 font-inter-normal-13px text-gray-800">
                  {queue.name}
                </span>
              }
              value={queue.id}
            />
          ))}
        </SimpleRadioGroup>
        <QueueWarningCallout
          queue={selectedQueue}
          sourceSchemaId={sourceSchemaId}
          sourceType={sourceType}
        />
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="select-queue-create-button"
            disabled={!selectedQueueId}
            htmlType="button"
            variant="primary"
            onClick={handleCreate}
          >
            Create a Case
          </Button>
        }
        secondaryButton="Cancel"
      />
    </Modal>
  );
};
